import type {ReactElement} from 'react';
import {useRef} from 'react';
import {Icon} from '@Components/icon-v2';
import {FlatIconColor, FlatIconColorType, IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {Text, TextSize} from '@Components/text';
import Divider from '@Components/divider';
import styles from './no-template-results.module.scss';
import useWindowSize from '@Hooks/useWindowSize';

interface NoTemplateResultsProps {
  search: string;
  isOpenedInMobileModal?: boolean;
  isCustomPosterType: boolean;
  areFiltersApplied: boolean;
  isGenericError?: boolean;
}

const BIG_SCREEN_THRESHOLD = 674;

export function NoTemplateResults({isOpenedInMobileModal = false, isGenericError = false, ...props}: NoTemplateResultsProps): ReactElement<any> {
  const {windowHeight} = useWindowSize();
  const searchInput = useRef(props.search);

  const getErrorText = (): string => {
    if (isGenericError) {
      return window.i18next.t('pmwjs_no_templates_found_text_generic');
    }
    if (searchInput.current !== '') {
      return window.i18next.t('pmwjs_no_templates_found_text');
    }

    if (props.isCustomPosterType && !props.areFiltersApplied) {
      return window.i18next.t('pmwjs_no_templates_found_text_custom_size');
    }

    return window.i18next.t('pmwjs_no_templates_found_text_generic');
  };

  const showErrorSubText = (): boolean => {
    if (isGenericError) {
      return false;
    }
    if (searchInput.current !== '') {
      return true;
    }

    return props.isCustomPosterType && !props.areFiltersApplied;
  };

  const getErrorMessageForSubText = (): string => {
    if (searchInput.current !== '') {
      return `'${searchInput.current}'`;
    }

    if (props.isCustomPosterType && !props.areFiltersApplied) {
      return `'${window.posterEditor?.whiteboard?.userWidth}${window.posterEditor?.whiteboard?.units} x ${window.posterEditor?.whiteboard?.userHeight}${window.posterEditor?.whiteboard?.units}'`;
    }

    return window.i18next.t('pmwjs_no_templates_found_text');
  };

  return (
    <div
      className={`flex-v-row ${windowHeight <= BIG_SCREEN_THRESHOLD || isOpenedInMobileModal ? 'flex-content-center _full-height' : 'spacing-m-t-16 _fit-height'} flex-items-center`}
    >
      <div>
        <Icon
          icon="icon-search"
          type={IconType.FLAT}
          flatIconType={{
            type: FlatIconColorType.DEFAULT,
            color: FlatIconColor.NEUTRAL,
          }}
          size={IconSize.SIZE_ICON_24}
          shape={IconShape.CIRCLE}
        />
      </div>
      <Text className={`spacing-m-t-5 text-center`} size={TextSize.XXSMALL} val={getErrorText()} />
      {showErrorSubText() ? <Text className="spacing-m-t-4 content-primary" bold size={TextSize.XSMALL} val={getErrorMessageForSubText()} /> : null}
      <Divider additionalClasses={`spacing-m-t-7 spacing-m-b-2 ${styles.divider}`} />
      <Text className="spacing-m-t-5" bold size={TextSize.SMALL} val={`${window.i18next.t('pmwjs_tips')}:`} />
      <ul className="spacing-m-t-5 flex-v-row flex-align-items-center">
        <li key="1" id="1" className="flex-h-row flex-items-center">
          <Icon icon="icon-filter-2" className="spacing-m-r-2" size={IconSize.SIZE_ICON_16} />
          <Text className="content-sub-text" size={TextSize.XXSMALL} val={window.i18next.t('pmwjs_no_templates_tips_1')} />
        </li>
        <li key="2" id="2" className="flex-h-row spacing-m-t-4 flex-items-center">
          <Icon icon="icon-search" className="spacing-m-r-2" size={IconSize.SIZE_ICON_16} />
          <Text className="content-sub-text" size={TextSize.XXSMALL} val={window.i18next.t('pmwjs_no_templates_tips_2')} />
        </li>
        <li key="3" id="3" className="flex-h-row spacing-m-t-4 flex-items-center">
          <Icon icon="icon-spellcheck" className="spacing-m-r-2" size={IconSize.SIZE_ICON_16} />
          <Text className="content-sub-text" size={TextSize.XXSMALL} val={window.i18next.t('pmwjs_no_templates_tips_3')} />
        </li>
      </ul>
    </div>
  );
}
